import { useFlags } from 'launchdarkly-react-client-sdk';
import { Link } from 'react-router';

import { SideBarPaddingWrapper, SideBarSectionContainer } from 'DesignSystem/features/sidebar';
import SidebarDetailContent from 'DesignSystem/features/sidebar/SidebarDetailContent';
import { Badge } from 'DesignSystem/ui/badge';
import { Button } from 'DesignSystem/ui/button';
import { DEFAULT_DISRUPTION_PARAMS } from 'pages/disruption/queue/constants';
import { convertFiltersToSearchParams } from 'pages/disruption/queue/utils';
import { DisruptionQueueParams } from 'pages/disruption/types/paginated-item-queue';
import { formatDate } from 'utils/helpers';

import { CustomerStatusField } from '../../components/customer-status-field';
import { useEventDetails } from '../event-detail.route';
import EventSeverityBadge from './event-severity-badge';
import EventStatusBadge from './event-status-badge';

export default function EventSidebar() {
  const { eventMitigationProject, hasDrmV2 } = useFlags();
  const data = useEventDetails();

  if (!data?.event) {
    return null;
  }

  const { event } = data;

  const legacyDefaultDisruptionQueueParams: DisruptionQueueParams = {
    ...DEFAULT_DISRUPTION_PARAMS,
    for_event: [event.event_id],
  };

  const disruptionQueueLink = hasDrmV2
    ? `/disruptions?page=1&page_size=25&days_calculated=30&for_event=${event.event_id}`
    : `/disruptions?${new URLSearchParams(convertFiltersToSearchParams(legacyDefaultDisruptionQueueParams)).toString()}`;

  const formattedPublishedDate =
    event.published_at && typeof event.published_at === 'string'
      ? formatDate(event.published_at, 'numeric', '2-digit', '2-digit')
      : 'Unknown';

  return (
    <SideBarPaddingWrapper>
      <SideBarSectionContainer>
        <SidebarDetailContent label="Event Type" classNames="text-nowrap">
          <div className="flex flex-wrap justify-end gap-1">
            {event.types.map((type) => (
              <Badge key={type} variant="informative" className="text-nowrap">
                {type}
              </Badge>
            ))}
          </div>
        </SidebarDetailContent>
      </SideBarSectionContainer>
      <SideBarSectionContainer>
        <SidebarDetailContent label="Announced">
          <div className="type-body-xs text-zinc-500">{formattedPublishedDate}</div>
        </SidebarDetailContent>
        <SidebarDetailContent label="Status">
          <EventStatusBadge />
        </SidebarDetailContent>
        {eventMitigationProject ? (
          <SidebarDetailContent label="Your Status">
            <CustomerStatusField
              eventId={event.event_id}
              status={event.customer_status}
              display={event.customer_status_display}
            />
          </SidebarDetailContent>
        ) : null}
        <SidebarDetailContent label="Severity">
          <EventSeverityBadge />
        </SidebarDetailContent>
        <SidebarDetailContent label="Affected Items">
          <div className="type-body-xs text-zinc-500">{event.products_affected} items</div>
        </SidebarDetailContent>
        <SidebarDetailContent label="Impacted Locations">
          <div className="type-body-xs text-zinc-500">{event.locations_impacted} Locations</div>
        </SidebarDetailContent>
      </SideBarSectionContainer>
      {!eventMitigationProject ? (
        <Link to={disruptionQueueLink}>
          <Button className="mt-3 w-full">View Affected Items</Button>
        </Link>
      ) : null}
    </SideBarPaddingWrapper>
  );
}
